import { ReactNode } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { isEmpty } from "lodash";
import { FieldDisplayType } from "@/pages/settings/components/dealsSettings/FieldManagement/types";
import {
  DreamerAttachmentColumn,
  DreamerTableRow
} from "@/entities/attachments/types";
import { simpleDate, simpleTime, toLocalDate } from "@/utils/datetime";
import { formatNumber, reducePrecision } from "@/utils/numbers";
import { getCurrencySymbol } from "@/pages/deals/helpers";
import { ChartDisplayType } from "@/components/global/Chat/chartKind";
import {
  formatCustomFieldValue,
  parseMultiSelectValue
} from "@/entities/dataTables/helpers";
import MultiSelectBadgesCell from "@/components/controls/DataTable/MultiSelectBadgesCell";
import UrlCell from "@/components/controls/DataTable/UrlCell";
import {
  DataTableFilter,
  FilterValue
} from "@/components/controls/DataTableFilters/DataTableFilters";

const NOT_AVAILABLE = "";

const renderNumericValue = (
  rawValue: unknown,
  column: DreamerAttachmentColumn
): ReactNode => {
  const numValue = parseFloat(String(rawValue));
  if (Number.isNaN(numValue)) return NOT_AVAILABLE;

  if (column.displayType === FieldDisplayType.PERCENTAGE) {
    const { value } = formatNumber(numValue * 100, 2, false, false);
    return `${value}%`;
  }

  if (column.displayType === ChartDisplayType.PERCENTAGE_0_TO_100) {
    return `${reducePrecision(numValue, 2)}%`;
  }

  if (column.displayType === FieldDisplayType.CURRENCY) {
    const { value } = formatNumber(numValue, 2, false, false);
    const currencyCode = column.currency || "USD";
    return `${getCurrencySymbol(currencyCode)}${value}`;
  }

  return formatNumber(numValue, 2, false, false).value;
};

// simpleDate's Intl formatter throws RangeError on an Invalid Date, which
// would take down the whole table for one malformed cell value.
const formatSafeDate = (rawValue: unknown): string => {
  const date = toLocalDate(rawValue as string);
  return Number.isNaN(date.getTime())
    ? NOT_AVAILABLE
    : simpleDate(date, "short");
};

const renderDateRange = (rawValue: unknown): ReactNode => {
  let range: { fromDate?: string; toDate?: string };
  if (typeof rawValue === "string") {
    try {
      range = JSON.parse(rawValue);
    } catch {
      return NOT_AVAILABLE;
    }
  } else {
    range = rawValue as { fromDate?: string; toDate?: string };
  }

  if (!range?.fromDate && !range?.toDate) return NOT_AVAILABLE;

  const from = range.fromDate ? formatSafeDate(range.fromDate) : "";
  const to = range.toDate ? formatSafeDate(range.toDate) : "";
  if (from && to) return `${from} - ${to}`;
  return from || to;
};

const renderCell = (
  column: DreamerAttachmentColumn,
  rawValue: unknown
): ReactNode => {
  if (column.displayType === FieldDisplayType.MULTI_SELECT) {
    const values = parseMultiSelectValue(rawValue);
    return values.length > 0 ? (
      <MultiSelectBadgesCell values={values} />
    ) : (
      NOT_AVAILABLE
    );
  }

  if (isEmpty(rawValue) && typeof rawValue !== "number") {
    return NOT_AVAILABLE;
  }

  switch (column.displayType) {
    case FieldDisplayType.NUMBER:
    case FieldDisplayType.CURRENCY:
    case FieldDisplayType.PERCENTAGE:
    case ChartDisplayType.PERCENTAGE_0_TO_100:
      return renderNumericValue(rawValue, column);

    case FieldDisplayType.DATE: {
      if (
        column.key.endsWith("_time") &&
        typeof rawValue === "string" &&
        rawValue.includes("T")
      ) {
        const parsed = new Date(rawValue);
        if (!isNaN(parsed.getTime())) return simpleTime(parsed);
      }
      return formatSafeDate(rawValue);
    }

    case FieldDisplayType.DATE_RANGE:
      return renderDateRange(rawValue);

    case FieldDisplayType.URL:
      return <UrlCell url={String(rawValue)} />;

    default: {
      const formatted = formatCustomFieldValue(rawValue, column.displayType);
      return formatted ?? NOT_AVAILABLE;
    }
  }
};

const filterTypeForColumn = (
  column: DreamerAttachmentColumn
): FilterValue | null => {
  switch (column.displayType) {
    case FieldDisplayType.NUMBER:
      return { type: "numeric" };
    case FieldDisplayType.PERCENTAGE:
      return { type: "numeric", isPercentage: true };
    case FieldDisplayType.CURRENCY:
      return { type: "numeric", isMonetary: true };
    case FieldDisplayType.DATE:
    case FieldDisplayType.DATE_RANGE:
      return { type: "date" };
    case FieldDisplayType.CHECKBOX:
    case FieldDisplayType.BOOL:
    case FieldDisplayType.SWITCH:
      return { type: "boolean", label: column.label };
    case FieldDisplayType.TEXT:
    case FieldDisplayType.TEXTAREA:
    case FieldDisplayType.EMAIL:
    case FieldDisplayType.URL:
      return { type: "text" };
    default:
      return { type: "text" };
  }
};

export const buildDreamerTableFilterOptions = (
  metadataColumns: DreamerAttachmentColumn[] | undefined
): DataTableFilter[] => {
  if (!metadataColumns) return [];
  return metadataColumns.flatMap<DataTableFilter>((column) => {
    const filterType = filterTypeForColumn(column);
    if (!filterType) return [];
    return [
      {
        key: column.key,
        customFieldLabel: column.label,
        filterType
      }
    ];
  });
};

export const buildDreamerTableColumns = (
  metadataColumns: DreamerAttachmentColumn[] | undefined,
  {
    size = 150,
    enableSorting = false
  }: { size?: number; enableSorting?: boolean } = {}
): ColumnDef<DreamerTableRow, unknown>[] => {
  if (!metadataColumns) return [];
  return metadataColumns.map((column) => ({
    id: column.key,
    accessorKey: column.key,
    header: column.label,
    size,
    enableSorting,
    meta: {
      disableTooltip: column.displayType === FieldDisplayType.URL
    },
    cell: ({ getValue }: { getValue: () => unknown }) =>
      renderCell(column, getValue())
  }));
};
